import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Boxes, CalendarDays, FolderKanban, MapPin, Palette, Users } from 'lucide-react';
import { brandkitApi, type BrandSummaryRow } from './api';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkBrand } from '@/lib/api/types';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { loadGoogleFont } from '@/lib/fonts/loadGoogleFont';
import { useBrandContextStore, useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { useT } from '@/lib/i18n/useT';
import type { TranslationKey } from '@/lib/i18n/translations';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';

const COUNTS = [
  { key: 'projects', icon: FolderKanban },
  { key: 'events', icon: CalendarDays },
  { key: 'stations', icon: MapPin },
  { key: 'merch', icon: Boxes },
  { key: 'contacts', icon: Users },
] as const;

/**
 * Una marca pintada con su propia identidad: la cabecera lleva su degradado
 * (o su imagen de fondo), el logotipo del kit y el nombre en su tipografía;
 * debajo, lo que tiene —proyectos, eventos, estaciones, merch, contactos— y
 * las dos salidas: abrir la marca o editar su identidad.
 *
 * Los colores del kit se quedan en la cabecera. El texto del cuerpo usa los
 * tokens de la app: una paleta de marca no garantiza contraste sobre nuestra
 * superficie, y una tarjeta que no se lee no sirve para elegir.
 */
export function BrandCard({
  brand,
  summary,
}: {
  brand: XkBrand;
  summary: BrandSummaryRow | undefined;
}) {
  const t = useT();
  const navigate = useNavigate();
  const selectedId = useSelectedBrandId();
  const setSelectedBrandId = useBrandContextStore((s) => s.setSelectedBrandId);
  const kitQ = useQuery({
    queryKey: queryKeys.brandKit.byCompany(brand.id),
    queryFn: () => brandkitApi.get(brand.id),
    staleTime: 60_000,
  });
  const kit = kitQ.data ?? null;

  const primary = kit?.primaryColor ?? brand.backgroundColor ?? 'var(--color-accent)';
  const secondary = kit?.secondaryColor ?? brand.secondaryColor ?? primary;
  const accent = kit?.accentColor ?? secondary;
  const logo = kit?.brandLogo ?? kit?.clientLogo ?? brand.logo ?? null;
  const backgroundImage = kit?.backgroundImage ?? brand.backgroundImage ?? null;
  const font = kit?.font ?? null;
  useEffect(() => loadGoogleFont(font), [font]);

  const isCurrent = selectedId === brand.id;
  const isRoot = brand.parentId == null;
  const initial = (brand.name ?? '?').charAt(0).toUpperCase();
  const abrir = () => {
    setSelectedBrandId(brand.id);
    navigate(`/brand/${brand.id}`);
  };
  const detalle = [brand.industry, font].filter(Boolean).join(' · ');

  return (
    <article
      data-testid="brand-card"
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border bg-[var(--color-bg-surface)] transition-shadow hover:shadow-lg',
        isCurrent ? 'border-[var(--color-accent-ink)]' : 'border-[var(--color-border-default)]',
      )}
    >
      <button
        type="button"
        onClick={abrir}
        aria-label={`${t('brandsGallery.open')}: ${brand.name ?? ''}`}
        className="relative block h-28 w-full text-left"
        style={{
          background: backgroundImage
            ? `url(${resolveImageUrl(backgroundImage)}) center / cover no-repeat`
            : `linear-gradient(135deg, ${primary}, ${secondary})`,
        }}
      >
        {backgroundImage && (
          <span
            aria-hidden
            className="absolute inset-0"
            style={{ background: 'linear-gradient(135deg, rgba(0,0,0,.35), rgba(0,0,0,.1))' }}
          />
        )}
        <span
          className="absolute -bottom-6 left-5 grid h-14 w-14 place-items-center overflow-hidden rounded-xl border-2 bg-white shadow-md"
          style={{ borderColor: accent }}
        >
          {logo ? (
            <img src={resolveImageUrl(logo)} alt="" className="h-full w-full object-contain p-1.5" />
          ) : (
            <span className="text-lg font-extrabold" style={{ color: primary }}>
              {initial}
            </span>
          )}
        </span>
        {isCurrent && (
          <span
            className="absolute right-3 top-3 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
            style={{ color: primary }}
          >
            {t('brandsGallery.current')}
          </span>
        )}
      </button>

      <div className="flex flex-1 flex-col gap-3 px-5 pb-4 pt-8">
        <div className="min-w-0">
          <h3
            className="flex items-center gap-2 text-lg font-bold text-[var(--color-fg-primary)]"
            style={font ? { fontFamily: `'${font}', sans-serif` } : undefined}
          >
            <span className="truncate">{brand.name}</span>
            {isRoot && (
              <span className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
                {t('brandsGallery.root')}
              </span>
            )}
          </h3>
          <p className="truncate text-xs text-[var(--color-fg-muted)]">
            {detalle || (kitQ.isLoading ? t('common.loading') : t('brandsGallery.noKit'))}
          </p>
        </div>

        <ul className="grid grid-cols-5 gap-1 rounded-xl bg-[var(--color-bg-elevated)] p-2">
          {COUNTS.map(({ key, icon: Icon }) => (
            <li key={key} className="flex flex-col items-center gap-0.5 py-1 text-center">
              <Icon className="h-3.5 w-3.5 text-[var(--color-fg-muted)]" aria-hidden />
              <span className="text-sm font-bold tabular-nums text-[var(--color-fg-primary)]">
                {summary ? summary[key] : '–'}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-[var(--color-fg-muted)]">
                {t(`brandsGallery.count.${key}` as TranslationKey)}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-auto flex items-center justify-between gap-2">
          <Button variant="primary" size="sm" onClick={abrir}>
            {t('brandsGallery.open')}
          </Button>
          <Link to={`/brand-studio/${brand.id}`}>
            <Button variant="ghost" size="sm">
              <Palette className="mr-1.5 h-3.5 w-3.5" />
              {t('brandsGallery.editIdentity')}
            </Button>
          </Link>
        </div>
      </div>
    </article>
  );
}
